import React, { FC, memo, useState } from "react";
import { Select } from "@montana/shared/form";
import { Button, ButtonStyles } from "@montana/shared/button";
import { MobileAndTabletLandscape, TabletLandscapeAndLarger } from "@montana/shared/responsive";
import { useCart } from "@montana/containers/cart";
import { Translate } from "@montana/shared/translate";
import { formatString } from "@montana/helpers/string-format/string-format.helper";
import { useTranslations } from "@montana/containers/translations";
import { Price } from "@montana/shared/price";
import { IPrice } from '../../../models/generated/cms.interface';
import { Link } from "@montana/shared/link";
import { ProductImage } from "@montana/shared/product-image/product-image.component";
import { SvgIcon } from "@montana/shared/svg-icon";
import { IGtmProductWithQuantity } from "@montana/services/tracking/tracking";
import { IAppCartLine } from "@montana/containers/cart/cart";
import { useTracking } from "@montana/containers/tracking";
import { getDimensionTranslationValue } from "@montana/helpers/product.helper";
import { staticSettings } from "@montana/helpers/static-settings.helper";

interface ICartItemHeadlineProps {
    name?: string;
    unitPrice?: IPrice;
    priceUnitWithoutDiscount?: IPrice;
    discount?: IPrice;
}

const CartItemHeadline: React.FC<ICartItemHeadlineProps> = React.memo(({ name, unitPrice, priceUnitWithoutDiscount, discount }: ICartItemHeadlineProps): JSX.Element => (
    <>
        {name &&
            <h3 className="cart-page-list-item__information-name">
                {name}
            </h3>
        }
        {unitPrice &&
            <div className="cart-page-list-item__information-price">
                <div className="cart-page-list-item__price-display">
                    <p className="cart-page-list-item__price-current"><Price obj={unitPrice} /></p>
                    {(!!discount?.amount && discount?.amount > 0) && (
                        <p className="cart-page-list-item__price-before">
                            <Price className="price" obj={priceUnitWithoutDiscount} />
                        </p>
                    )}
                </div>
                {staticSettings.b2b &&
                    <p className="cart-page-list-item__suggested-retail-price"><Translate value="general.suggestedRetailPrice" defaultValue="[Suggested retail price]" /></p>
                }
                {(!!discount?.amount && discount?.amount > 0) && (
                    <p className="cart-page-list-item__price-saved">
                        <span><Translate value={(t): string => t.cartPage.cartPageSave} defaultValue="[Save]" />&nbsp;</span>
                        <Price obj={discount} />
                    </p>
                )}
            </div>
        }
    </>
));

interface ICartItemSpecificationProps {
    label: string | undefined;
    value: string | undefined;
}

const CartItemSpecification: FC<ICartItemSpecificationProps> = memo<ICartItemSpecificationProps>(({ label, value }) => (
    <div className="cart-page-list-item__specifications-list-item">
        <dt>{label}</dt>
        <dd>{value}</dd>
    </div>
));

interface ICartItemPropertiesProps {
    line: IAppCartLine;
    size?: string;
}

const CartItemProperties: React.FC<ICartItemPropertiesProps> = React.memo(({ line }: ICartItemPropertiesProps): JSX.Element | null => {
    const translate = useTranslations();

    return line.properties ? (
        <div className="cart-page-list-item__specifications">
            <h3 className="cart-page-list-item__specifications-title">
                <Translate value={(t): string => t.cartPage.cartPageSpecifications} defaultValue="[Specifications]" />
            </h3>
            <dl>
                {line.properties.filter(p => !!p.label && !!p.text).map((property, index) => (
                    <CartItemSpecification key={index} label={property.label} value={getDimensionTranslationValue(translate, property.text)} />
                ))}
                {staticSettings.b2b &&
                    <>
                        <CartItemSpecification label={translate(t => t.cartPage.cartPageItemNumber, "[Item number]")} value={line.parentProductCode} />
                        <CartItemSpecification label={translate(t => t.cartPage.cartPageEan, "[EAN]")} value={line.ean} />
                    </>
                }
            </dl>
        </div>
    ) : null;
});

interface ICartItemAmount {
    amount: number;
    productGroupId?: number;
}

const CartItemAmount: React.FC<ICartItemAmount> = React.memo(({ amount, productGroupId }: ICartItemAmount): JSX.Element => {
    const [amounts, setAmount] = useState(amount);

    const translate = useTranslations();
    const { updateCartLineQuantity } = useCart();

    const handleAmountUpdate = (val: string) => {
        setAmount(Number(val));
        if (productGroupId) {
            updateCartLineQuantity(productGroupId, Number(val), true);
        }
    };

    const options = [
        {
            text: `1 ${translate(t => t.cartPage.cartPagePiece, '[piece]')}`,
            value: '1',
        }
    ];

    for (let index = 0; index < amount + 5; index++) {
        options.push({
            text: `${index + 2} ${translate(t => t.cartPage.cartPagePieces, '[pieces]')}`,
            value: String(index + 2),
        });
    }

    return (
        <div className="cart-page-list-item__price-amount">
            <Select
                value={String(amounts)}
                onChange={handleAmountUpdate}
                options={options}
            />
        </div>
    )
});

interface ICartItemPriceProps {
    price?: IPrice;
}

const CartItemPrice: React.FC<ICartItemPriceProps> = React.memo(({ price }: ICartItemPriceProps): JSX.Element => (
    <p className="cart-page-list-item__price-total">
        <Price obj={price} />
    </p>
));

export const CartLines: React.FC = React.memo((): JSX.Element => {
    const { linesToDisplay, updateCartLineQuantity, reinstateCartLine, updateQuantityLoading } = useCart();
    const { trackRemoveFromCart } = useTracking();

    const removeFromCart = (product: IAppCartLine) => {
        const gtmProducts: IGtmProductWithQuantity[] = [{
            id: product.code || '',
            name: product.name || '',
            price: product.price?.amount || 0,
            quantity: product.quantity,
            category: product.category?.categoryName || '',
        }];
        trackRemoveFromCart(product.price?.currency || '', gtmProducts);

        updateCartLineQuantity(product.productGroupId, 0);
    };

    return (
        <ul>
            {linesToDisplay && linesToDisplay.map(line => (
                <li key={line.code} className="cart-page-list-item">
                    {line.pendingDeletion &&
                        <div className="cart-page-list-item__remove col">
                            <p className="font-pro-bold max-w-2/3 mx-auto pt-2">
                                <Translate value={(t): string => t.cartPage.itemWasRemovedFromBasket} defaultValue="{0} was removed from your basket">
                                    {(t): string => formatString(t, `${line.name}`)}
                                </Translate>
                            </p>
                            <div className="p-2">
                                <Button disabled={updateQuantityLoading} className="tracking-wider" onClick={() => reinstateCartLine(line.productGroupId)} style={ButtonStyles.Text}>
                                    <Translate value={(t): string => t.cartPage.getItemBack} defaultValue="Get it back" />
                                </Button>
                            </div>
                        </div>
                    }
                    {/* Because of somewhat big layout changes from mobile to tablet and up, we need to have lots of different rows and columns.
                            So be aware of content that might exist in two places, and make sure you make changes for both layouts */}
                    <div className="cart-page-list-item__content">
                        <MobileAndTabletLandscape>
                            <div className="cart-page-list-item__main">
                                <Link to={line.productUrl || undefined}>
                                    <CartItemHeadline name={line.name} unitPrice={line.unitPrice} priceUnitWithoutDiscount={line.priceUnitWithoutDiscount} discount={line.discount} />
                                </Link>

                                {(!!line.properties?.length || staticSettings.b2b) && (
                                    <CartItemProperties line={line} />
                                )}
                            </div>

                            <div className="cart-page-list-item__image-wrapper">
                                <Link to={line.productUrl || undefined}>
                                    <ProductImage productImage={line.productImageViewModel} name={line.name} sizes="130px" />
                                </Link>
                            </div>

                            <div className="w-full">
                                <div className="row items-center">
                                    <div className="col">
                                        <div className="cart-page-list-item__amount-wrapper">
                                            <CartItemAmount amount={line.quantity} productGroupId={line.productGroupId} />
                                        </div>
                                    </div>
                                    <div className="col">
                                        <Button
                                            className="cart-page-list-item__price-delete"
                                            disabled={updateQuantityLoading}
                                            disableStroke={true}
                                            style={ButtonStyles.Text}
                                            onClick={() => line.productGroupId && removeFromCart(line)}
                                        >
                                            <Translate value={(t): string => t.cartPage.cartPageDelete} defaultValue="[Delete]" />
                                            <SvgIcon iconName="close" />
                                        </Button>
                                    </div>
                                    <div className="col ml-auto">
                                        <CartItemPrice price={line.price} />
                                    </div>
                                </div>
                            </div>
                        </MobileAndTabletLandscape>

                        <TabletLandscapeAndLarger>
                            <div className="cart-page-list-item__image">
                                <Link to={line.productUrl || undefined}>
                                    <ProductImage productImage={line.productImageViewModel} name={line.name} sizes="280px" />
                                </Link>
                            </div>

                            <div className="cart-page-list-item__main">
                                <div className="cart-page-list-item__information">
                                    <Link to={line.productUrl || undefined}>
                                        <CartItemHeadline name={line.name} unitPrice={line.unitPrice} priceUnitWithoutDiscount={line.priceUnitWithoutDiscount} discount={line.discount} />
                                    </Link>
                                </div>

                                {(!!line.properties?.length || staticSettings.b2b) && (
                                    <CartItemProperties line={line} />
                                )}
                            </div>

                            <div className="cart-page-list-item__price">
                                <Button
                                    className="cart-page-list-item__price-delete"
                                    disabled={updateQuantityLoading}
                                    disableStroke={true}
                                    style={ButtonStyles.Text}
                                    onClick={() => line.productGroupId && removeFromCart(line)}
                                >
                                    <Translate value={(t): string => t.cartPage.cartPageDelete} defaultValue="[Delete]" />
                                    <SvgIcon iconName="close" />
                                </Button>
                                <div>
                                    <CartItemAmount amount={line.quantity} productGroupId={line.productGroupId} />
                                    <CartItemPrice price={line.price} />
                                    {staticSettings.b2b &&
                                        <div className="cart-page-list-item__vat-text">
                                            <Translate value={(t): string => t.cartPage.taxIncluded} defaultValue="[Incl. VAT]" />
                                        </div>
                                    }
                                </div>
                            </div>
                        </TabletLandscapeAndLarger>
                    </div>
                </li>
            ))}
        </ul >
    );
});
